import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import { Pressable, View } from "react-native";

import { Icon } from "@/components/ui/icon";
import { Text } from "@/components/ui/text";
import { TEST_IDS } from "@/testIdDefinition";
import { type FontAwesome6ProRegularIconName } from "@react-native-vector-icons/fontawesome6-pro";

type HomeRequestActionsProps = {
    disabled?: boolean;
};

type RequestAction = {
    key: "leave" | "medical" | "pickup";
    href: "/leave-request" | "/medical-request" | "/pickup-request";
    icon: FontAwesome6ProRegularIconName;
    labelKey:
        | "home.requestLeave"
        | "home.requestMedical"
        | "home.requestPickup";
    testId: string;
};

const ACTIONS_ORDERED_KEYS: ActionKey[] = ["leave", "medical", "pickup"].filter(
    (key) => CONFIG.featureMedicalRequestsEnabled || key !== "medical",
) as ActionKey[];
type ActionKey = "leave" | "medical" | "pickup";

export const QUICK_REQUEST_ACTIONS: Record<ActionKey, RequestAction> = {
    leave: {
        key: "leave",
        href: "/leave-request",
        icon: "person-walking",
        labelKey: "home.requestLeave",
        testId: TEST_IDS[TEST_IDS.HOME_NEW_LEAVE_REQUEST_BUTTON],
    },
    medical: {
        key: "medical",
        href: "/medical-request",
        icon: "pills",
        labelKey: "home.requestMedical",
        testId: TEST_IDS[TEST_IDS.HOME_NEW_MEDICAL_REQUEST_BUTTON],
    },
    pickup: {
        key: "pickup",
        href: "/pickup-request",
        icon: "car-side",
        labelKey: "home.requestPickup",
        testId: TEST_IDS[TEST_IDS.HOME_NEW_PICKUP_REQUEST_BUTTON],
    },
};

const ACTIONS = ACTIONS_ORDERED_KEYS.map(
    (key: ActionKey) => QUICK_REQUEST_ACTIONS[key],
);

export function HomeRequestActions({
    disabled = false,
}: HomeRequestActionsProps) {
    const { t } = useTranslation();
    const router = useRouter();

    return (
        <View className="flex-row items-stretch gap-4">
            {ACTIONS.map((action) => (
                <Pressable
                    key={action.key}
                    disabled={disabled}
                    onPress={() => router.push(action.href)}
                    testID={action.testId}
                    accessibilityRole="button"
                    accessibilityLabel={t(action.labelKey)}
                    accessibilityState={{ disabled }}
                    className="min-h-20 flex-1 items-center justify-center gap-1 rounded-card bg-card py-4 shadow-elevated active:bg-accent"
                >
                    <Icon
                        name={action.icon}
                        iconStyle="solid"
                        className={
                            disabled ? "text-muted-foreground" : "text-primary"
                        }
                        size={20}
                    />
                    <Text
                        variant="caption"
                        className={
                            disabled ? "text-muted-foreground" : "text-primary"
                        }
                    >
                        {t(action.labelKey)}
                    </Text>
                </Pressable>
            ))}
        </View>
    );
}
